import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { BuscaRemota, type DefCampo } from '../../componentes/formulario';
import { nomeParceiro, nomePessoa } from '../../nucleo/formato';

/**
 * Lista de 03/11, item 3: "mais de uma empresa e mais de um contato" no formulário de Criar negociação. Este bloco é a lista dos
 * "outros": uma busca no Cadastros que acrescenta ao fim da lista, a lista com o X de cada um e o "Criar" que cadastra sem sair
 * (o mesmo `DialogoCriacao` do "Adicionar" do campo principal). A primeira empresa (o campo) é a principal; estas entram como
 * adicionais. Serve às empresas e aos contatos (`pessoa`).
 */

export const MAX_OUTRAS_EMPRESAS = 20;

export interface Vinculo {
  id: string;
  nome: string;
}

/**
 * As empresas da criação: a principal é a primeira (o campo; sem ele, a primeira das outras) e as demais são adicionais, sem repetir.
 * Função pura, testada em `tests/lista0311-item3.test.ts`.
 */
export function empresasDaCriacao(principal: string | null, outras: string[]): { principal: string | null; adicionais: string[] } {
  const ids = [...new Set([...(principal ? [principal] : []), ...outras])];
  return { principal: ids[0] ?? null, adicionais: ids.slice(1) };
}

/** Filiais: as outras empresas do grupo para marcar, sem as que já estão em "Outras empresas". Função pura. */
export function grupoParaMarcar(grupo: Vinculo[], outras: Vinculo[]): Vinculo[] {
  return grupo.filter((g) => !outras.some((x) => x.id === g.id));
}

/** Acrescenta ao fim sem repetir (nem o que já é o principal, em `jaTem`). Função pura. */
export function comOutro(lista: Vinculo[], novo: Vinculo, jaTem: ReadonlySet<string> = new Set()): Vinculo[] {
  return jaTem.has(novo.id) || lista.some((x) => x.id === novo.id) ? lista : [...lista, novo];
}

export function OutrosVinculos({
  tipo,
  itens,
  aoAdicionar,
  aoTirar,
  jaTem,
  busca,
  pessoa = false,
  criar,
  limite,
}: {
  tipo: 'empresa' | 'contato';
  itens: Vinculo[];
  aoAdicionar: (v: Vinculo) => void;
  aoTirar: (id: string) => void;
  /** O que já está na negociação por fora desta lista (o campo principal): não entra de novo. */
  jaTem: ReadonlySet<string>;
  busca: NonNullable<DefCampo['busca']>;
  pessoa?: boolean;
  /** O "Criar": abre o cadastro rápido (ausente sem permissão ou sem o módulo). */
  criar?: () => void;
  limite?: number;
}) {
  const { t } = useTranslation('pipeline');
  const id = useId();
  const idBusca = `${id}-busca`;
  // A busca recomeça vazia a cada um que entra: o `key` troca e o foco volta a ela (adicionar vários só com o teclado).
  const [rodada, setRodada] = useState(0);
  const voltarFoco = useRef(false);
  useEffect(() => {
    if (voltarFoco.current) document.getElementById(idBusca)?.focus();
    voltarFoco.current = false;
  }, [rodada, idBusca]);
  const mostrar = (nome: string) => (pessoa ? nomePessoa(nome) : nomeParceiro(nome));
  const cheio = limite !== undefined && itens.length >= limite;
  const rotulos = tipo === 'empresa' ? { legenda: t('crm.criarNeg.outrasEmpresas'), ajuda: t('crm.criarNeg.outrasEmpresasAjuda'), buscar: t('crm.criarNeg.buscarOutraEmpresa'), criar: t('crm.criarNeg.criarOutraEmpresa') } : { legenda: t('crm.criarNeg.outrosContatos'), ajuda: t('crm.criarNeg.outrosContatosAjuda'), buscar: t('crm.criarNeg.buscarOutroContato'), criar: t('crm.criarNeg.criarOutroContato') };
  return (
    <div className="flex min-w-0 flex-col gap-2 sm:col-span-6" data-outros-vinculos={tipo}>
      <p className="text-sm font-medium text-tinta-2" id={`${id}-titulo`}>
        {rotulos.legenda}
      </p>
      {itens.length > 0 && (
        <ul aria-labelledby={`${id}-titulo`} className="divide-y divide-borda rounded-lg border border-borda bg-sup-2">
          {itens.map((x) => (
            <li key={x.id} className="flex items-center justify-between gap-2 px-3 py-1" data-outro-vinculo={x.id}>
              <span className="min-w-0 truncate text-sm" title={mostrar(x.nome)}>
                {mostrar(x.nome)}
              </span>
              <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.criarNeg.tirarDaLista', { nome: mostrar(x.nome) })} title={t('crm.criarNeg.tirarDaLista', { nome: mostrar(x.nome) })} icone={<X className="size-4" />} onClick={() => aoTirar(x.id)} data-tirar-outro="" />
            </li>
          ))}
        </ul>
      )}
      {!cheio && (
        <div className="min-w-0" data-buscar-outro={tipo}>
          <label htmlFor={idBusca} className="mb-1.5 block text-sm font-medium text-tinta-2">
            {rotulos.buscar}
          </label>
          <BuscaRemota
            key={rodada}
            id={idBusca}
            def={busca}
            valor={null}
            aoMudar={(v, item) => {
              if (!v) return;
              voltarFoco.current = true;
              // Quem já é o principal (o campo) não entra de novo na lista; a busca só recomeça.
              if (!jaTem.has(v)) aoAdicionar({ id: v, nome: item ? busca.rotulo(item) : '' });
              setRodada((x) => x + 1);
            }}
          />
        </div>
      )}
      <p className="text-xs text-tinta-3">{rotulos.ajuda}</p>
      {criar && !cheio && (
        <button type="button" data-adicionar={`outro-${tipo}`} onClick={criar} className="flex min-h-11 w-fit items-center gap-1.5 text-sm font-medium text-acento-forte hover:underline md:min-h-9">
          <Plus className="size-4" aria-hidden />
          {rotulos.criar}
        </button>
      )}
    </div>
  );
}

/** O selo "Principal" ao lado da dica da empresa principal do formulário. */
export function DicaPrincipal() {
  const { t } = useTranslation('pipeline');
  return (
    <p className="flex items-start gap-2 text-xs text-tinta-3 sm:col-span-6" data-empresa-principal-dica="">
      <Selo cor="azul">{t('crm.criarNeg.selePrincipal')}</Selo>
      <span className="min-w-0">{t('crm.criarNeg.empresaPrincipalAjuda')}</span>
    </p>
  );
}
